import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
  Quote,
  QuoteRequest,
  QuoteTask,
  QuoteTaskInput,
  AllianceQuoteRequest,
  RawQuote,
  RawQuoteRequest,
  UserAllianceQuoteRequest,
  transformRawQuoteToQuote,
} from '../types/quote';
import { useNotificationStore } from './notificationStore';
import { useJobStore } from './jobStore';
import { useCreditStore } from './creditStore';

type QuoteAllianceWithService = {
  invited_freelancer_id: string;
  service_category_id?: number | null;
};

interface QuoteState {
  quotes: Quote[];
  quoteRequests: QuoteRequest[];
  allianceQuoteRequests: AllianceQuoteRequest[];
  quoteRequestsLoading: boolean;
  allianceQuoteRequestsLoading: boolean;
  loading: boolean;
  error: string | null;
  fetchQuoteIdByJobId: (jobId: string) => Promise<string | undefined>;

  fetchQuotes: () => Promise<void>;
  fetchQuoteById: (quoteId: string) => Promise<Quote | null>;
  fetchQuoteRequests: () => Promise<void>;
  fetchAllianceQuoteRequests: () => Promise<void>;

  createQuote: (params: {
    jobId: string;
    message?: string;
    parentQuoteId?: string | null;
  }) => Promise<Quote | undefined>;

  retireQuote: (quoteId: string) => Promise<void>;
  activateQuote: (quoteId: string) => Promise<void>;
  updateQuoteStatus: (quoteId: string, status: Quote['status']) => Promise<void>;

  acceptQuoteRequest: (quoteRequestId: string) => Promise<void>;
  declineQuoteRequest: (quoteRequestId: string, message: string) => Promise<void>;

  acceptAllianceQuoteRequest: (quoteRequestId: string) => Promise<void>;
  declineAllianceQuoteRequest: (quoteRequestId: string, message: string) => Promise<void>;

  addQuoteWithTasks: (params: {
    jobId: string;
    tasks: QuoteTaskInput[];
    message?: string;
    parentQuoteId?: string | null;
    quoteId?: string;
    deletedTaskIds?: string[];
  }) => Promise<Quote | undefined>;

  addQuoteWithAlliances: (params: {
    jobId: string;
    alliances: { invited_freelancer_id: string; service_category_id?: number | null }[]; // <- MAJ
    parentQuoteId?: string | null;
    quoteId?: string;
    deletedAllianceIds?: string[];
  }) => Promise<Quote | undefined>;

  submitQuote: (quoteId: string) => Promise<void>;

  requestQuoteChange: (quoteId: string, message: string) => Promise<void>;

  acceptQuote: (quoteId: string) => Promise<void>;
  rejectQuote: (quoteId: string, message: string) => Promise<void>;

  inviteQuoteAlliance: (
    quoteId: string,
    invitedFreelancerId: string,
    serviceCategoryId?: number | null,
    requestMessage?: string | null
  ) => Promise<void>;

  retireQuoteAlliance: (quoteAllianceId: string) => Promise<void>;

  updateQuoteAllianceServiceCategory: (
    quoteAllianceId: string,
    serviceCategoryId: number | null
  ) => Promise<void>;
}

export const useQuoteStore = create<QuoteState>((set, get) => ({
  quotes: [],
  quoteRequests: [],
  allianceQuoteRequests: [],
  loading: false,
  error: null,
  quoteRequestsLoading: false,
  allianceQuoteRequestsLoading: false,

  fetchQuotes: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const { data, error } = await supabase.rpc('fetch_user_quotes');
      if (error) throw error;
      const quotes = (data || []).map((q: RawQuote) => {
        return transformRawQuoteToQuote(q);
      });
      set({ quotes: quotes as Quote[], error: null });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  fetchQuoteById: async (quoteId: string) => {
    try {
      const { data, error } = await supabase.rpc('fetch_quote_by_id', {
        p_quote_id: quoteId,
      });
      if (error) throw error;
      const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
      if (!row) {
        set({ error: 'Quote not found' });
        return null;
      }
      const quote = transformRawQuoteToQuote(row as RawQuote);
      set(state => ({
        quotes: state.quotes.some(q => String(q.id) === String(quoteId))
          ? state.quotes.map(q => (String(q.id) === String(quoteId) ? quote : q))
          : [...state.quotes, quote],
        error: null,
      }));
      return quote;
    } catch (err) {
      set({ error: (err as Error).message });
      return null;
    }
  },

  fetchQuoteRequests: async () => {
    if (get().quoteRequestsLoading) return;

    set({ quoteRequestsLoading: true });
    try {
      const { data, error } = await supabase.rpc('fetch_user_quote_requests');
      if (error) throw error;

      const quoteRequests = data.map((request: RawQuoteRequest) => ({
        id: request.id,
        created_at: request.created_at,
        job: {
          id: request.job_id,
          title: request.job_title,
          description: request.job_description,
          published_at: request.job_published_at,
          start_date: request.job_start_date,
          end_date: request.job_end_date,
          on_site: request.job_on_site,
          languages: request.job_languages || [],
          city: { id: request.city_id, name: request.city_name },
          client: {
            id: request.client_id,
            first_name: request.client_first_name,
            company_name: request.client_company_name,
            avatar_url: request.client_avatar_url,
          },
          budget_range: {
            id: request.budget_range_id,
            code: request.budget_range_code,
            min_amount: request.budget_range_min_amount,
            max_amount: request.budget_range_max_amount,
          },
          required_services: request.required_services || [],
          status: request.job_status,
        },
        status: request.status,
        expires_at: request.created_at,
        has_quote: request.has_quote,
      }));

      set({ quoteRequests: quoteRequests as QuoteRequest[], error: null });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ quoteRequestsLoading: false });
    }
  },

  fetchAllianceQuoteRequests: async () => {
    if (get().allianceQuoteRequestsLoading) return;

    set({ allianceQuoteRequestsLoading: true });
    try {
      const { data, error } = await supabase.rpc('fetch_user_alliance_quote_requests');
      if (error) throw error;

      const allianceQuoteRequests = data.map((request: UserAllianceQuoteRequest) => ({
        id: request.id,
        created_at: request.created_at,
        request_message: request.request_message ?? null,
        job: {
          id: request.job_id,
          title: request.job_title,
          description: request.job_description,
          published_at: request.job_published_at,
          start_date: request.job_start_date,
          end_date: request.job_end_date,
          on_site: request.job_on_site,
          languages: request.job_languages || [],
          city: { id: request.city_id, name: request.city_name },
          client: {
            id: request.client_id,
            first_name: request.client_first_name,
            company_name: request.client_company_name,
            avatar_url: request.client_avatar_url,
          },
          budget_range: {
            id: request.budget_range_id,
            code: request.budget_range_code,
            min_amount: request.budget_range_min_amount,
            max_amount: request.budget_range_max_amount,
          },
          required_services: request.required_services || [],
        },
        parent_quote_owner: {
          id: request.parent_quote_owner_id,
          first_name: request.parent_quote_owner_first_name,
          last_name: request.parent_quote_owner_last_name,
          avatar_url: request.parent_quote_owner_avatar_url,
        },
        parent_quote_id: request.parent_quote_id,
        status: request.status,
        has_quote: request.has_quote,
      }));

      set({ allianceQuoteRequests: allianceQuoteRequests as AllianceQuoteRequest[], error: null });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ allianceQuoteRequestsLoading: false });
    }
  },

  createQuote: async (params: {
    jobId: string;
    message?: string;
    parentQuoteId?: string | null;
  }) => {
    const { jobId, message, parentQuoteId } = params;
    const { data, error } = await supabase.rpc('create_quote', {
      p_job_id: jobId,
      p_message: message || null,
      p_parent_quote_id: parentQuoteId || null,
    });
    if (error) throw error;
    await get().fetchQuotes();
    return get().quotes.find(q => q.id === data);
  },

  retireQuote: async (quoteId: string) => {
    const { error } = await supabase.rpc('retire_quote', { p_quote_id: quoteId });
    if (error) throw error;
    await get().fetchQuotes();
  },

  activateQuote: async (quoteId: string) => {
    const { error } = await supabase.rpc('activate_quote', { p_quote_id: quoteId });
    if (error) throw error;
    await get().fetchQuoteById(quoteId);
  },

  updateQuoteStatus: async (quoteId: string, status: Quote['status']) => {
    set({ loading: true });
    try {
      const { error } = await supabase.from('quotes').update({ status }).eq('id', quoteId);
      if (error) throw error;
      set(state => ({
        quotes: state.quotes.map(quote => (quote.id === quoteId ? { ...quote, status } : quote)),
      }));
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  updateQuoteRequest: async (
    requestId: string,
    status: QuoteRequest['status'],
    message?: string
  ) => {
    set({ loading: true });
    try {
      const { error } = await supabase
        .from('quote_requests')
        .update({ status, message })
        .eq('id', requestId);
      if (error) throw error;
      set(state => ({
        quoteRequests: state.quoteRequests.map(request =>
          request.id === requestId ? { ...request, status, message } : request
        ),
      }));
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  acceptQuoteRequest: async (quoteRequestId: string) => {
    const { error } = await supabase.rpc('respond_to_quote_request', {
      p_quote_request_id: quoteRequestId,
      p_status: 'accepted',
    });
    if (error) throw error;
    // Refresh credit balance after acceptance (in case credits were consumed)
    useCreditStore.getState().refreshBalance();
    useNotificationStore.getState().fetchUnreadCount();
    await get().fetchQuoteRequests();
  },

  declineQuoteRequest: async (quoteRequestId: string, message: string) => {
    const { error } = await supabase.rpc('respond_to_quote_request', {
      p_quote_request_id: quoteRequestId,
      p_status: 'rejected',
      p_reject_reason: message,
    });
    if (error) throw error;
    useNotificationStore.getState().fetchUnreadCount();
    await get().fetchQuoteRequests();
  },

  addQuoteWithTasks: async ({ jobId, tasks, message, parentQuoteId, quoteId, deletedTaskIds }) => {
    const formattedTasks = tasks.map(task => {
      const base = {
        name: task.name,
        pricing_type: task.pricing_type,
        quantity: task.quantity,
        unit_price: task.unit_price,
      };
      if (task.id) return { ...base, task_id: task.id };
      return base;
    });

    const { data, error } = await supabase.rpc('add_quote_with_tasks', {
      p_job_id: jobId,
      p_tasks: formattedTasks,
      p_message: message ?? null,
      p_parent_quote_id: parentQuoteId || null,
      p_quote_id: quoteId || null,
      p_deleted_task_ids: deletedTaskIds || null,
    });
    if (error) throw error;

    const payload = typeof data === 'object' && data !== null && 'id' in data ? data : null;
    const quoteIdFromRpc = payload ? payload.id : (data as string);

    await get().fetchQuotes();
    const quote = get().quotes.find(q => q.id === quoteIdFromRpc);
    if (!quote) return undefined;

    if (
      typeof payload === 'object' &&
      payload !== null &&
      Array.isArray(payload.tasks) &&
      payload.tasks.length > 0
    ) {
      const tasksFromRpc = payload.tasks as Array<{
        id: string;
        name: string;
        description?: string | null;
        pricing_type: string;
        quantity: number;
        unit_price: number;
        total_amount: number | null;
        sort_order: number | null;
      }>;
      return {
        ...quote,
        tasks: tasksFromRpc.map(
          (t): QuoteTask => ({
            id: t.id,
            name: t.name,
            description: t.description ?? undefined,
            pricing_type: t.pricing_type as QuoteTask['pricing_type'],
            quantity: t.quantity ?? 0,
            unit_price: t.unit_price ?? 0,
            total_amount: t.total_amount ?? 0,
            sort_order: t.sort_order ?? 0,
          })
        ),
      };
    }

    return quote;
  },

  addQuoteWithAlliances: async ({
    jobId,
    alliances,
    parentQuoteId,
    quoteId,
    deletedAllianceIds,
  }) => {
    const formattedAlliances: QuoteAllianceWithService[] = alliances.map(
      ({ invited_freelancer_id, service_category_id }) =>
        ({
          invited_freelancer_id,
          ...(service_category_id !== undefined ? { service_category_id } : {}),
        }) as QuoteAllianceWithService
    );

    const { data, error } = await supabase.rpc('add_quote_with_alliances', {
      p_job_id: jobId,
      p_alliances: formattedAlliances,
      p_parent_quote_id: parentQuoteId || null,
      p_quote_id: quoteId || null,
      p_deleted_alliance_ids: deletedAllianceIds || null,
    });
    if (error) throw error;

    await get().fetchQuotes();
    return get().quotes.find(q => q.id === data);
  },

  acceptAllianceQuoteRequest: async (quoteRequestId: string) => {
    const { error } = await supabase.rpc('respond_to_alliance_quote_request', {
      p_quote_alliance_id: quoteRequestId,
      p_status: 'accepted',
    });
    if (error) throw error;
    // Refresh credit balance after acceptance (in case credits were consumed)
    useCreditStore.getState().refreshBalance();
    useNotificationStore.getState().fetchUnreadCount();
    await get().fetchAllianceQuoteRequests();
  },

  declineAllianceQuoteRequest: async (quoteRequestId: string, message: string) => {
    const { error } = await supabase.rpc('respond_to_alliance_quote_request', {
      p_quote_alliance_id: quoteRequestId,
      p_status: 'rejected',
      p_reject_reason: message,
    });
    if (error) throw error;
    useNotificationStore.getState().fetchUnreadCount();
    await get().fetchAllianceQuoteRequests();
  },

  submitQuote: async (quoteId: string) => {
    const { error } = await supabase.rpc('submit_quote', { p_quote_id: quoteId });
    if (error) throw error;
    useCreditStore.getState().refreshBalance();
    await get().fetchQuoteById(quoteId);
  },

  requestQuoteChange: async (quoteId: string, message: string) => {
    const { error } = await supabase.rpc('request_quote_change', {
      p_quote_id: quoteId,
      p_message: message,
    });
    if (error) throw error;
    await get().fetchQuoteById(quoteId);
    await useJobStore.getState().fetchJobs();
  },

  acceptQuote: async (quoteId: string) => {
    const { error } = await supabase.rpc('accept_quote', { p_quote_id: quoteId });
    if (error) throw error;
  },

  rejectQuote: async (quoteId: string, message: string) => {
    const { error } = await supabase.rpc('reject_quote', {
      p_quote_id: quoteId,
      p_reject_message: message,
    });
    if (error) throw error;
  },

  retireQuoteAlliance: async (quoteAllianceId: string) => {
    const { error } = await supabase.rpc('retire_quote_alliance', {
      p_quote_alliance_id: quoteAllianceId,
    });
    if (error) throw error;

    const principalQuoteId = get().quotes.find(q =>
      q.alliances?.some(a => String(a.id) === String(quoteAllianceId))
    )?.id;
    if (principalQuoteId) {
      await get().fetchQuoteById(principalQuoteId);
    } else {
      await get().fetchQuotes();
    }
  },

  inviteQuoteAlliance: async (
    quoteId: string,
    invitedFreelancerId: string,
    serviceCategoryId?: number | null,
    requestMessage?: string | null
  ): Promise<void> => {
    const params: Record<string, unknown> = {
      p_quote_id: quoteId,
      p_invited_freelancer_id: invitedFreelancerId,
    };
    if (serviceCategoryId !== undefined) params.p_service_category_id = serviceCategoryId;
    if (requestMessage !== undefined) params.p_request_message = requestMessage;

    const { error } = await supabase.rpc('invite_quote_alliance', params);
    if (error) throw error;

    await get().fetchQuoteById(quoteId);
  },

  fetchQuoteIdByJobId: async (jobId: string) => {
    try {
      const { data, error } = await supabase.rpc('fetch_quote_id_by_job', {
        p_job_id: jobId,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        return data[0].quote_id;
      }

      return undefined;
    } catch (error) {
      console.error('Error fetching quote ID by Job ID:', error);
      set({ error: (error as Error).message });
      return undefined;
    }
  },

  updateQuoteAllianceServiceCategory: async (
    quoteAllianceId: string,
    serviceCategoryId: number | null
  ) => {
    const { error } = await supabase
      .from('quote_alliances')
      .update({
        service_category_id: serviceCategoryId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', quoteAllianceId);

    if (error) throw error;

    const principalQuoteId = get().quotes.find(q =>
      q.alliances?.some(a => String(a.id) === String(quoteAllianceId))
    )?.id;
    if (principalQuoteId) {
      await get().fetchQuoteById(principalQuoteId);
    } else {
      await get().fetchQuotes();
    }
  },
}));
